import { useEffect, useId, useState } from 'react';

export function SliderField({
    label,
    value,
    min,
    max,
    step = 1,
    onChange,
    disabled = false,
    format = String,
    formatBound = String,
    limitMin = null,
    limitMax = null,
    children,
}) {
    const id = useId();
    const [draft, setDraft] = useState(() => format(value));

    // Track the slider unless the user is mid-edit in the text box.
    useEffect(() => setDraft(format(value)), [value, format]);

    const percent = (v) => Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100));

    const showMin = limitMin != null && limitMin > min && limitMin < max;
    const showMax = limitMax != null && limitMax > min && limitMax < max;
    // const violated = (limitMin != null && value < limitMin) || (limitMax != null && value > limitMax);
    const violated = false;

    // On each keystroke, re-format the live-typed value; on blur, also clamp and commit it.
    const updateDraft = (raw, commit = false) => {
        const stripped = String(raw).replace(/[^0-9.]/g, '');

        // Don't reformat mid-decimal-entry (e.g. "80."), or an empty field, unless committing.
        if (!commit && (stripped === '' || stripped.endsWith('.'))) {
            setDraft(stripped);
            return;
        }

        const parsed = stripped === '' ? NaN : Number(stripped);
        if (!Number.isFinite(parsed)) {
            setDraft(commit ? format(value) : stripped);
            return;
        }

        const resolved = commit ? Math.min(max, Math.max(min, parsed)) : parsed;
        if (commit) onChange(resolved);
        setDraft(format(resolved));
    };

    return (
        <div className={`col_field slider_field${violated ? ' error_field' : ''}`}>
            <div className="field_title">
                <label htmlFor={id}>{label}</label>
                <input
                    className="ui-text"
                    type="text"
                    inputMode="numeric"
                    aria-label={`${label} value`}
                    value={draft}
                    disabled={disabled}
                    onChange={(e) => updateDraft(e.target.value)}
                    onBlur={(e) => updateDraft(e.target.value, true)}
                    onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                />
            </div>

            <div className="slider_boxer">
                <div className="slider_track">
                    {showMin && (
                        <div className="slider_highlight_min" style={{ width: `${percent(limitMin)}%` }}>
                            <span>{formatBound(limitMin)}</span>
                        </div>
                    )}
                    {showMax && (
                        <div className="slider_highlight_max" style={{ width: `${100 - percent(limitMax)}%` }}>
                            <span>{formatBound(limitMax)}</span>
                        </div>
                    )}
                    <input
                        id={id}
                        className="slider_input"
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        disabled={disabled}
                        onChange={(e) => onChange(Number(e.target.value))}
                    />
                </div>
                <div className="slider_value_min">{formatBound(min)}</div>
                <div className="slider_value_max">{formatBound(max)}</div>
            </div>

            {children}
        </div>
    );
}

/** "5 M" / "100 K", as numberToUnit() renders the slider bounds. */
export function formatCompact(value) {
    if (value >= 1_000_000) {
        const m = value / 1_000_000;
        return `${Number.isInteger(m) ? m : m.toFixed(1)} M`;
    }
    if (value >= 1_000) {
        const k = value / 1_000;
        return `${Number.isInteger(k) ? k : k.toFixed(1)} K`;
    }
    return String(value);
}

export const formatMoney = (value) => `$${Number(value).toLocaleString('en-US')}`;
